import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { waitForProcessingComplete } from './processingStatusHelpers';

/**
 * Trigger generation of the Semble MDT Report AI paragraphs (Well Life Clinic).
 *
 * Calls the `generateSembleMdtReport` Cloud Function, which writes up to four
 * reviewable paragraphs into `dataModel.6_manageReports.sections.10` in the
 * standard outputs/inputs/approvedParagraphs shape.  The clinician then
 * reviews them on the RD_SembleMDT page before the export document can be
 * rendered.
 *
 * Called automatically by the Diagnostic Decision completion flow, and
 * manually from the Documents Dashboard row (Generate button for legacy
 * patients / staleness resets, Retry after errors).
 *
 * @param {Object} params
 * @param {string} params.clinicianId - The authenticated clinician's UID.
 * @param {string} params.patientId - The patient document ID.
 * @param {string[]} [params.forceRegenerateKeys] - Optional array of paragraph
 *   codes to regenerate selectively (e.g. ['semble_patient_goals']).  When
 *   provided, only those paragraphs are regenerated and only their approvals
 *   cleared — all other paragraphs are preserved unchanged.  Used for dirty
 *   re-completion when only specific inputs changed (e.g. goals notes).
 * @param {boolean} [params.waitAndRetry=false] - When true and another
 *   generation is already in-flight, wait for it to complete and then retry
 *   once (matching the cover-page / patient-report wait-then-retry pattern).
 *   Callers that fire from exit handlers (RD_CI, CIPrefilledPage, MDT Plan
 *   re-completion) should set this to true so concurrent staleness signals
 *   are not silently dropped.
 *
 * Uses a transactional guard on `sembleMDTReportProcessingStatus` so
 * concurrent callers don't double-invoke the Cloud Function.  Fire-and-forget:
 * returns once processing is set; the CF writes the generated paragraphs +
 * final status on completion.
 */
export const TriggerSembleMdtReport = async ({ clinicianId, patientId, forceRegenerateKeys, waitAndRetry = false }) => {
  const generateSembleMdtReport = httpsCallable(functions, 'generateSembleMdtReport', {
    timeout: 360000, // 6 minutes — slightly above the server-side 5-min timeout
  });

  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'Not authenticated' };
  if (!clinicianId || !patientId) {
    console.error('Missing required parameters:', { clinicianId, patientId });
    return { success: false, error: 'Missing required parameters' };
  }

  try {
    const patientDocRef = doc(db, 'patients', patientId);

    // Transactional guard: only one caller wins the race to set 'processing'.
    let alreadyProcessing = false;
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(patientDocRef);
      const manageReports = snap.data()?.dataModel?.['6_manageReports'];
      if (manageReports?.sembleMDTReportProcessingStatus === 'processing') {
        alreadyProcessing = true;
        return;
      }
      transaction.update(patientDocRef, {
        'dataModel.6_manageReports.sembleMDTReportProcessingStatus': 'processing',
        'dataModel.6_manageReports.sembleMDTReportProcessingStartedAt': serverTimestamp(),
      });
    }, { maxAttempts: 10 });

    if (alreadyProcessing) {
      if (!waitAndRetry) {
        console.log('Skipping Semble MDT Report generation — already processing');
        return { success: true, data: { status: 'already-processing' } };
      }

      // Wait-then-retry: wait for the in-flight generation to complete, then
      // trigger one more time.  This mirrors the cover-page and patient-report
      // helpers so concurrent staleness signals (e.g. physical-health exit
      // racing CI-review exit) are not silently lost.
      console.log('Semble MDT Report already processing — waiting for completion before retry');
      await waitForProcessingComplete(patientId, 'sembleMDTReportProcessingStatus');

      // Retry exactly once — no infinite loops.  The retry does not itself
      // use waitAndRetry, so a third concurrent caller would still bail.
      console.log('Semble MDT Report processing complete — retrying generation');
      return TriggerSembleMdtReport({ clinicianId, patientId, forceRegenerateKeys, waitAndRetry: false });
    }

    // Fire-and-forget: invoke the CF without awaiting its result.
    // forceRegenerateKeys enables targeted regeneration (e.g. only the goals
    // paragraph when the clinician edits goals and re-completes the DD).
    const payload = { clinicianId, patientId };
    if (forceRegenerateKeys && forceRegenerateKeys.length > 0) {
      payload.forceRegenerateKeys = forceRegenerateKeys;
    }
    generateSembleMdtReport(payload)
      .then((result) => console.log('Semble MDT Report paragraphs generated:', result.data))
      .catch((error) => {
        console.error('Error in generateSembleMdtReport function:', error);
        // deadline-exceeded means the client timed out, but the CF is still
        // running server-side and will write its final status when done.
        if (error.code === 'deadline-exceeded') {
          console.log('Client-side timeout; function continues processing in background');
          return;
        }
        updateDoc(patientDocRef, {
          'dataModel.6_manageReports.sembleMDTReportProcessingStatus': 'error',
        }).catch((updateError) => console.error('Error updating processing status:', updateError));
      });

    return { success: true, data: { status: 'processing' } };
  } catch (error) {
    console.error('Error in TriggerSembleMdtReport transaction:', error);
    return { success: false, error };
  }
};
